import React, { Fragment } from 'react';
import { IconButton, lighten, TextField, Theme, Toolbar, Tooltip, Typography } from '@mui/material';

import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';

import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';

const useToolbarStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(1),
        },
        highlight:
            theme.palette.mode === 'light'
                ? {
                    color: theme.palette.secondary.main,
                    backgroundColor: lighten(theme.palette.secondary.light, 0.85),
                }
                : {
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.secondary.dark,
                },
        title: {
            flex: '1 1 100%',
        },
        filterContainer: {
            display: 'flex',
            alignItems: 'center',
            [theme.breakpoints.down('sm')]: {
                width: '100%',
                marginTop: theme.spacing(1),
            },
        },
        filterInput: {
            [theme.breakpoints.down('sm')]: {
                flexGrow: 1,
            },
        },
    }),
);

interface EditionTableToolbarProps {
    onFilterChanged?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> | undefined;
    numSelected: number;
    headerLabel: string;
    onEdit: React.MouseEventHandler<HTMLButtonElement>;
    onDelete: React.MouseEventHandler<HTMLButtonElement>;
    onAdd: React.MouseEventHandler<HTMLButtonElement>;
}

export default function EditionTableToolbar(props: EditionTableToolbarProps) {
    const classes = useToolbarStyles();
    const { numSelected } = props;
    const { t } = useTranslation("common");

    return (
        <Toolbar
            className={clsx(classes.root, {
                [classes.highlight]: numSelected > 0,
            })}
            sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
        >
            {numSelected > 0 ? (
                <Typography className={classes.title} color="inherit" variant="subtitle1" component="div">
                    {`${numSelected} ${t("commonLabels.selected")}`}
                </Typography>
            ) : (
                <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
                    {props.headerLabel}
                </Typography>
            )}

            {numSelected === 1 ? (
                <Tooltip title={"" + t("commonLabels.edit")}>
                    <IconButton aria-label={t<string>("commonLabels.edit")} onClick={props.onEdit} size="large">
                        <EditIcon />
                    </IconButton>
                </Tooltip>
            ) : (<></>)}

            {numSelected > 0 ? (
                <Tooltip title={t("commonLabels.delete").toString()}>
                    <IconButton
                        aria-label={t<string>("commonLabels.delete")}
                        onClick={props.onDelete}
                        size="large">
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <Tooltip title={t("commonLabels.add").toString()}>
                    <IconButton aria-label={t<string>("commonLabels.add")} onClick={props.onAdd} size="large">
                        <AddIcon />
                    </IconButton>
                </Tooltip>
            )}
            {props.onFilterChanged ? (
                <Fragment>
                    <div className={classes.filterContainer} data-testid="table-toolbar-filter">
                        <TextField className={classes.filterInput} label={t("rider.filter")} id="itemfilter" onChange={props.onFilterChanged} />
                        <Tooltip title="Filter list">
                            <IconButton aria-label="filter list" size="large">
                                <FilterListIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                </Fragment>
            ) : null}
        </Toolbar>
    );
}